// Google Tag Manager custom events. Every event is pushed to window.dataLayer
// (GTM container is loaded in public/index.html). Ecommerce events use the
// GA4 recommended names and `ecommerce` shape so GA4 tags can forward them
// with "Send Ecommerce data" enabled.
//
// Never push PII (names, emails, messages) — GA4 prohibits it.

const push = (payload) => {
    try {
        window.dataLayer = window.dataLayer || [];
        // Clear the previous ecommerce object so values don't bleed between events.
        if(payload.ecommerce) {
            window.dataLayer.push({ ecommerce: null });
        }
        window.dataLayer.push(payload);
    } catch(e) {
        console.error('Analytics push failed:', e);
    }
};

const toNumber = (value) => {
    let num = parseFloat(value);
    return isNaN(num) ? 0 : Math.round(num * 100) / 100;
};

// Items carry only what GA4 needs for monetization reports: handle, unit price, quantity.
const item = (handle, price, quantity) => ({ item_id: handle, price: toNumber(price), quantity: quantity });

const itemsValue = (items) => toNumber(items.reduce((total, i) => total + i.price * i.quantity, 0));

const Analytics = {
    addToCart: (entries, { source, packageKey } = {}) => {
        let items = entries
            .filter(e => e.product && e.quantity > 0)
            .map(e => item(e.product.handle, e.product.variants?.[0]?.price ?? e.product.price, e.quantity));
        if(items.length === 0) {
            return;
        }
        push({
            event: 'add_to_cart',
            add_source: source,
            quiz_package: packageKey,
            ecommerce: {
                currency: entries[0].product.variants?.[0]?.currencyCode || 'USD',
                value: itemsValue(items),
                items: items,
            },
        });
    },
    quizStart: ({ source }) => {
        push({ event: 'quiz_start', quiz_source: source });
    },
    quizAnswer: ({ source, question, answer, quantity }) => {
        push({
            event: 'quiz_question_answered',
            quiz_source: source,
            quiz_question: question,
            quiz_answer: answer,
            quiz_answer_quantity: quantity,
        });
    },
    quizComplete: ({ source, bathrooms, totalAreas, answers, recommendedPackage }) => {
        push({
            event: 'quiz_complete',
            quiz_source: source,
            quiz_bathrooms: bathrooms,
            quiz_total_areas: totalAreas,
            quiz_answers: answers,
            quiz_recommended_package: recommendedPackage,
        });
    },
    beginCheckout: (cart) => {
        let items = (cart?.lineItems || []).map(line => item(line.handle, line.variant?.price?.amount, line.quantity));
        push({
            event: 'begin_checkout',
            ecommerce: {
                currency: cart?.subtotalPrice?.currencyCode || 'USD',
                value: toNumber(cart?.subtotalPrice?.amount),
                items: items,
            },
        });
    },
    contactSubmit: ({ form }) => {
        push({ event: 'contact_form_submit', form_name: form });
    },
    // Dealer recruiting (/careers) funnel: CTA click → first keystroke → lead
    dealerCtaClick: ({ location }) => {
        push({ event: 'dealer_cta_click', cta_location: location });
    },
    dealerFormStart: () => {
        push({ event: 'dealer_form_start' });
    },
    dealerLeadSubmit: ({ businessType, salesModel, teamSize }) => {
        push({
            event: 'dealer_lead_submit',
            dealer_business_type: businessType || undefined,
            dealer_sales_model: salesModel || undefined,
            dealer_team_size: teamSize || undefined,
        });
    },
};

export default Analytics;
